import { useMemo } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis
} from 'recharts';

interface ChartRendererProps {
  placeholder: string;
}

export default function ChartRenderer({ placeholder }: ChartRendererProps) {
  const chartData = useMemo(() => {
    try {
      // Extraire le type et le subtype du placeholder
      // Format: {{CHART:TYPE:SUBTYPE:{"key":"value"}}}
      // On doit gérer les accolades imbriquées dans le JSON
      const startMatch = placeholder.match(/\{\{CHART:(\w+):(\w+):/);
      if (!startMatch) {
        // Essayer un format sans subtype (ex: MATRIX avec {})
        const simpleMatch = placeholder.match(/\{\{CHART:(\w+):(\w+):(\{\})\}\}\}/);
        if (simpleMatch) {
          return { type: simpleMatch[1].toUpperCase(), subtype: simpleMatch[2], config: {} };
        }
        console.warn('No match found for placeholder:', placeholder.substring(0, 100));
        return null;
      }

      const [, type, subtype] = startMatch;
      
      // Trouver le début du JSON (après le troisième :)
      const jsonStart = placeholder.indexOf(':', placeholder.indexOf(':', placeholder.indexOf(':') + 1) + 1) + 1;
      
      // Extraire le JSON en comptant les accolades
      let braceCount = 0;
      let jsonEnd = jsonStart;
      let inString = false;
      let escapeNext = false;
      
      for (let i = jsonStart; i < placeholder.length - 1; i++) {
        const char = placeholder[i];
        
        if (escapeNext) {
          escapeNext = false;
          continue;
        }
        
        if (char === '\\') {
          escapeNext = true;
          continue;
        }
        
        if (char === '"' && !escapeNext) {
          inString = !inString;
          continue;
        }
        
        if (!inString) {
          if (char === '{') {
            braceCount++;
          } else if (char === '}') {
            braceCount--;
            if (braceCount === 0) {
              jsonEnd = i + 1;
              break;
            }
          }
        }
      }
      
      const configStr = placeholder.substring(jsonStart, jsonEnd);
      
      // Parser le JSON de configuration
      let config: any = {};
      try {
        config = JSON.parse(configStr);
      } catch (e) {
        // Si le JSON est invalide, essayer une extraction manuelle avec regex
        console.warn('JSON parse failed, trying manual extraction:', e, 'Config string:', configStr.substring(0, 200));
        
        // Extraction manuelle pour les cas simples
        const phasesMatch = configStr.match(/"phases":\s*\[(.*?)\]/s);
        const valuesMatch = configStr.match(/"values":\s*\[(.*?)\]/s);
        const monthsMatch = configStr.match(/"months":\s*\[(.*?)\]/s);
        const axesMatch = configStr.match(/"axes":\s*\[(.*?)\]/s);
        const titleMatch = configStr.match(/"title":\s*"([^"]+)"/);
        const stepsMatch = configStr.match(/"steps":\s*\[(.*?)\]/s);
        
        if (titleMatch) config.title = titleMatch[1];
        
        if (phasesMatch) {
          const phases = phasesMatch[1].match(/"([^"]+)"/g)?.map(s => s.replace(/"/g, '')) || [];
          config.phases = phases;
        }
        if (valuesMatch) {
          const values = valuesMatch[1].match(/[\d.]+/g)?.map(v => parseFloat(v)) || [];
          config.values = values;
        }
        if (monthsMatch) {
          const months = monthsMatch[1].match(/"([^"]+)"/g)?.map(s => s.replace(/"/g, '')) || [];
          config.months = months;
        }
        if (axesMatch) {
          const axes = axesMatch[1].match(/"([^"]+)"/g)?.map(s => s.replace(/"/g, '')) || [];
          config.axes = axes;
        }
        if (stepsMatch) {
          const steps = stepsMatch[1].match(/"([^"]+)"/g)?.map(s => s.replace(/"/g, '')) || [];
          config.steps = steps;
        }
        
        // Si on n'a rien extrait, retourner null
        if (!config.title && !config.phases && !config.values && !config.months && !config.axes && !config.steps) {
          console.error('Failed to extract any config data from:', configStr.substring(0, 200));
          return null;
        }
      }

      return { type: type.toUpperCase(), subtype: subtype.toUpperCase(), config };
    } catch (error) {
      console.error('Error parsing chart placeholder:', error, 'Placeholder:', placeholder.substring(0, 100));
      return null;
    }
  }, [placeholder]);

  if (!chartData) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-sm text-red-600">Erreur: Impossible de parser le graphique</p>
        <code className="text-xs text-red-500 mt-2 block">{placeholder.substring(0, 100)}...</code>
      </div>
    );
  }

  const { type, subtype, config } = chartData;

  // BAR Chart
  if (type === 'BAR') {
    const data = config.phases?.map((phase: string, idx: number) => ({
      name: phase,
      value: config.values?.[idx] || 0
    })) || [];

    return (
      <div className="w-full h-80 my-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">
          {config.title || 'Graphique en barres'}
        </h4>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis 
              dataKey="name" 
              tick={{ fill: '#6b7280', fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis tick={{ fill: '#6b7280', fontSize: 12 }} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#fff', 
                border: '1px solid #e5e7eb',
                borderRadius: '8px'
              }}
            />
            <Bar 
              dataKey="value" 
              fill="#6366f1" 
              radius={[8, 8, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // LINE Chart
  if (type === 'LINE') {
    const data = config.months?.map((month: string, idx: number) => ({
      name: month,
      value: config.values?.[idx] || 0
    })) || [];

    return (
      <div className="w-full h-80 my-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">
          {config.title || 'Graphique linéaire'}
        </h4>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis 
              dataKey="name" 
              tick={{ fill: '#6b7280', fontSize: 12 }}
            />
            <YAxis tick={{ fill: '#6b7280', fontSize: 12 }} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#fff', 
                border: '1px solid #e5e7eb',
                borderRadius: '8px'
              }}
            />
            <Line 
              type="monotone" 
              dataKey="value" 
              stroke="#6366f1" 
              strokeWidth={3}
              dot={{ fill: '#6366f1', r: 5 }}
              activeDot={{ r: 7 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // SPIDER Chart (Radar)
  if (type === 'SPIDER' && subtype === 'MATURITY') {
    const axes = config.axes || [];
    const values = config.values || [];
    
    // Préparer les données pour le radar chart
    const data = axes.map((axis: string, idx: number) => ({
      axis,
      value: values[idx] || 0,
      fullMark: 5
    }));

    return (
      <div className="w-full h-80 my-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">
          {config.title || 'Graphique radar'}
        </h4>
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={data}>
            <PolarGrid stroke="#e5e7eb" />
            <PolarAngleAxis 
              dataKey="axis" 
              tick={{ fill: '#6b7280', fontSize: 11 }}
            />
            <PolarRadiusAxis 
              angle={90} 
              domain={[0, 5]} 
              tick={{ fill: '#6b7280', fontSize: 10 }}
            />
            <Radar
              name="Maturité"
              dataKey="value"
              stroke="#6366f1"
              fill="#6366f1"
              fillOpacity={0.6}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#fff', 
                border: '1px solid #e5e7eb',
                borderRadius: '8px'
              }}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // FLOWCHART - Pour l'instant, on affiche juste un placeholder visuel
  if (type === 'FLOWCHART') {
    const steps = config.steps || [];
    
    return (
      <div className="w-full my-6 p-6 bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl border-2 border-violet-200">
        <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">
          {config.title || 'Organigramme'}
        </h4>
        <div className="flex flex-wrap justify-center items-center gap-4">
          {steps.map((step: string, idx: number) => (
            <div key={idx} className="flex items-center gap-4">
              <div className="bg-violet-600 text-white px-6 py-3 rounded-lg font-semibold shadow-lg min-w-[150px] text-center">
                {step}
              </div>
              {idx < steps.length - 1 && (
                <div className="text-violet-600 font-bold text-xl">→</div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // MATRIX - Pour l'instant, on affiche juste un placeholder
  if (type === 'MATRIX') {
    return (
      <div className="w-full my-6 p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200">
        <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">
          Matrice
        </h4>
        <p className="text-sm text-gray-600 text-center">
          La matrice sera rendue lors de la présentation
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
      <p className="text-sm text-yellow-600">Type de graphique non supporté: {type}</p>
    </div>
  );
}

